import React from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'

import PDComponent from '../../pd-component'
import CustomDropdown from '../../components/custom-dropwdown'

export const TextOnlyQuickLinks = ({
    regions,
    textLinkAlignment,
    dontShowSlider = false,
    useDropdownForMobile = false,
    dropdownLabel,
    dropdownTextColor = 'black',
    dropdownBgColor = 'white',
    typeId,
    name
}) => {
    const totalCards = regions.reduce(
        (acc, region) =>
            acc + (region?.components?.filter((component) => component?.data?.linkUrl) || 0),
        0
    )
    if (!totalCards) {
        return null
    }

    const dropdownItems = regions.flatMap((regions) =>
        (regions?.components ?? [])
            .filter((component) => component?.data?.linkUrl && component?.data?.linkLabel)
            .map((component) => ({
                label: component?.data?.linkLabel,
                url: component?.data?.linkUrl
            }))
    )

    const nonDropdownDisplay = useDropdownForMobile ? {base: 'none', md: 'flex'} : 'flex'

    const dropdown = {
        textColor: dropdownTextColor === 'black' ? 'black.100' : dropdownTextColor,
        type: dropdownTextColor === 'black' ? 'light' : 'dark'
    }

    const stackProps = dontShowSlider ? {sx: {'.button-link': {flexGrow: 'unset !important'}}} : {}
    return (
        <Box
            width={{
                base: dontShowSlider ? 'unset' : '100vw',
                lg: dontShowSlider ? 'unset' : 'calc(100vw - var(--scrollbar-width))'
            }}
            minHeight={useDropdownForMobile ? {base: '44px', md: '54px'} : '54px'}
            className="text-links-box"
            sx={{
                '.swiper': {
                    paddingRight: {base: '15px', lg: '60px'},
                    paddingTop: '15px',
                    display: nonDropdownDisplay,

                    '&:not(.swiper-initialized)': {
                        paddingRight: {base: '15px', lg: '60px'},

                        '.swiper-wrapper': {
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '10px'
                        }
                    }
                },
                '.swiper-wrapper': {
                    paddingLeft: {base: '15px', lg: '60px'},
                    marginRight: {base: '-15px', lg: '-60px'},
                    alignItems: 'center'
                },
                '.swiper-slide': {
                    display: 'flex',
                    flexGrow: textLinkAlignment === 'center' ? '1' : 'unset',
                    width: textLinkAlignment === 'center' ? 'unset' : 'fit-content',
                    '&:last-child': {
                        marginRight: {
                            base: '15px !important',
                            lg: '60px !important'
                        }
                    }
                },
                '.swiper-button-prev, .swiper-button-next': {
                    display: 'none'
                }
            }}
        >
            {useDropdownForMobile && dropdownItems.length && (
                <Box
                    display={{base: 'flex', md: 'none'}}
                    flexWrap="wrap"
                    justifyContent="center"
                    gap="10px"
                    className="links-flex"
                    {...stackProps}
                    width="100%"
                >
                    <CustomDropdown
                        text={dropdownLabel}
                        links={dropdownItems}
                        fontFamily="inter"
                        buttonTextColor={dropdown.textColor}
                        buttonBgColor={dropdownBgColor}
                        type={dropdown.type}
                        variant="medium"
                        typeId={typeId}
                        name={name}
                    />
                </Box>
            )}

            {dontShowSlider ? (
                <Box
                    width={{base: 'unset', lg: '80%'}}
                    display={
                        useDropdownForMobile
                            ? {base: 'none', md: 'flex'}
                            : {base: 'grid', md: 'flex'}
                    }
                    gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                    flexWrap="wrap"
                    justifyContent="left"
                    gap={{base: '15px', md: '10px'}}
                    className="links-flex"
                    {...stackProps}
                    marginLeft={{
                        base: '15px',
                        lg: textLinkAlignment === 'center' ? 'auto' : '60px'
                    }}
                    marginRight={{
                        base: '15px',
                        lg: textLinkAlignment === 'center' ? 'auto' : '0px'
                    }}
                    paddingTop="15px"
                    flexDirection={{base: 'column', md: 'row'}}
                    sx={{
                        '.chakra-link, .chakra-text': {
                            width: {base: '100%', md: '150px'}
                        },
                        '.button-link:last-child:nth-child(odd)': {
                            gridColumn: '1 / -1',
                            justifySelf: 'center',
                            width: {base: '50%', md: 'unset'}
                        }
                    }}
                >
                    {regions?.map((region) =>
                        region?.components?.map(
                            (component, index) =>
                                component.data.linkUrl && (
                                    <PDComponent
                                        key={index}
                                        index={index}
                                        textLinkAlignment={textLinkAlignment}
                                        component={component}
                                    />
                                )
                        )
                    )}
                </Box>
            ) : (
                <Swiper
                    navigation={true}
                    modules={[Navigation]}
                    slidesPerView={'auto'}
                    spaceBetween={10}
                >
                    {regions?.map((region) =>
                        region?.components?.map((component, index) =>
                            component.data.linkUrl ? (
                                <SwiperSlide key={`${region.id}-${index}`}>
                                    <PDComponent
                                        key={index}
                                        index={index}
                                        textLinkAlignment={textLinkAlignment}
                                        component={component}
                                    />
                                </SwiperSlide>
                            ) : null
                        )
                    )}
                </Swiper>
            )}
        </Box>
    )
}

TextOnlyQuickLinks.propTypes = {
    regions: PropTypes.array,
    dontShowSlider: PropTypes.bool,
    textLinkAlignment: PropTypes.string,
    useDropdownForMobile: PropTypes.bool,
    dropdownLabel: PropTypes.string,
    dropdownTextColor: PropTypes.string,
    dropdownBgColor: PropTypes.string,
    typeId: PropTypes.string,
    name: PropTypes.any
}

export default TextOnlyQuickLinks
